import React from "react";
import {
  ChevronDown,
  ChevronRight,
  MapPin,
  Clock,
  Users,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ManuscriptMetadata } from "@/routes/innovation-days-demo/types";

interface WorldAndSettingContentProps {
  settings: ManuscriptMetadata["settings"];
}

export function WorldAndSettingContent({
  settings,
}: WorldAndSettingContentProps) {
  const [openSettings, setOpenSettings] = React.useState<number[]>([]);

  const toggleSetting = (index: number) => {
    setOpenSettings((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  // Calculate max-height for smooth animation
  const getMaxHeight = (isOpen: boolean) => {
    return isOpen ? "500px" : "0px";
  };

  return (
    <div className="space-y-5">
      {settings.map((setting, index) => {
        const isOpen = openSettings.includes(index);
        const isPrimary = setting.setting_type === "primary";

        return (
          <div
            key={`${setting.location}-${setting.time_period}`}
            className="relative pl-4"
          >
            {/* Left accent bar - spans entire card including expanded content */}
            <div
              className="absolute left-0 top-0 bottom-0 w-1 rounded-full transition-all duration-300"
              style={{
                background: isPrimary ? "#6366F1" : "#909AF9",
                opacity: isOpen ? 1 : 0.4,
              }}
            />

            <button
              type="button"
              onClick={() => toggleSetting(index)}
              className="w-full text-left group relative"
            >
              {/* Collapsed view - always visible */}
              <div className="space-y-2">
                {/* Header with labels */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      className="text-[11px] leading-[16px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border-transparent"
                      style={{
                        background: isPrimary ? "#EEF2FF" : "#F1F5F9",
                        color: isPrimary ? "#6366F1" : "#64748B",
                      }}
                    >
                      {isPrimary ? "Primary" : "Major"}
                    </Badge>
                    {setting.labels.map((label) => (
                      <span
                        key={label}
                        className="text-[13px] font-medium"
                        style={{ color: "#64748B" }}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-[#64748B] group-hover:text-[#6366F1] transition-colors flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-[#64748B] group-hover:text-[#6366F1] transition-colors flex-shrink-0" />
                  )}
                </div>

                {/* Quick preview - only in collapsed state */}
                {!isOpen && (
                  <p
                    className="text-[14px] leading-[20px] line-clamp-2"
                    style={{ color: "#64748B" }}
                  >
                    {setting.location}
                  </p>
                )}
              </div>
            </button>

            {/* Expanded view with smooth animation */}
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight: getMaxHeight(isOpen),
                opacity: isOpen ? 1 : 0,
              }}
            >
              <div className="mt-4 space-y-4 pb-1">
                {/* Location */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <MapPin
                      className="w-3.5 h-3.5"
                      style={{ color: "#6366F1" }}
                    />
                    <span
                      className="text-[12px] font-semibold uppercase tracking-wide"
                      style={{ color: "#64748B" }}
                    >
                      Location
                    </span>
                  </div>
                  <p
                    className="text-[15px] leading-[22px]"
                    style={{ color: "#475569" }}
                  >
                    {setting.location}
                  </p>
                </div>

                {/* Time Period */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Clock
                      className="w-3.5 h-3.5"
                      style={{ color: "#6366F1" }}
                    />
                    <span
                      className="text-[12px] font-semibold uppercase tracking-wide"
                      style={{ color: "#64748B" }}
                    >
                      Time Period
                    </span>
                  </div>
                  <p
                    className="text-[15px] leading-[22px]"
                    style={{ color: "#475569" }}
                  >
                    {setting.time_period}
                  </p>
                </div>

                {/* Social Structure */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Users
                      className="w-3.5 h-3.5"
                      style={{ color: "#6366F1" }}
                    />
                    <span
                      className="text-[12px] font-semibold uppercase tracking-wide"
                      style={{ color: "#64748B" }}
                    >
                      Social & Cultural
                    </span>
                  </div>
                  <p
                    className="text-[15px] leading-[22px]"
                    style={{ color: "#475569" }}
                  >
                    {setting.social_structure}
                  </p>
                </div>

                {/* Metaphysics */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Sparkles
                      className="w-3.5 h-3.5"
                      style={{ color: "#6366F1" }}
                    />
                    <span
                      className="text-[12px] font-semibold uppercase tracking-wide"
                      style={{ color: "#64748B" }}
                    >
                      Metaphysics
                    </span>
                  </div>
                  <p
                    className="text-[15px] leading-[22px]"
                    style={{ color: "#475569" }}
                  >
                    {setting.metaphysics}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
